/**
 * @file   src\Pages\AddNewPackage.tsx
 * @brief  Add New Package page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { React, Container, Breadcrumb, Button, Row, Col, Form, useOutletContext, useNavigate, useEffect, useState,  useParams } from '../utils/thirdpartyComponents';
import CWInput from '../components/CWInput';
import CWTextarea from '../components/CWTextarea';
import CWInputGroup from '../components/CWInputGroup';
import Fileimgicon from '../assets/images/photo-icon.svg';
import { pageURLs } from '../utils/pageUrls';
import { AppContextType } from '../interfaces/generalInterface';
import { PACKAGE_DESCRIPTION_LENGTH, PACKAGE_NAME_LENGTH, PACKAGE_PRICE_LENGTH } from '../utils/constants';
import * as Yup from 'yup'
import { addPackageSchema } from '../validations/addPackageSchema';
import SmallLoader from '../components/SmallLoader';
import axios from '../config/axiosConfig';
import { CommonApi, PackageApi } from '../utils/apiUrls';
import { MessageToaster } from '../utils/toastUtil';
import { validateImageTypes } from '../utils/helper';
import CloseIcon from '../assets/images/icons/close';

const AddNewPackage = (props: any) => {
    // Access the context
    const {  updateData } = useOutletContext<AppContextType>();
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false);

    const params = useParams()

    const [formData, setFormData] = useState({
        packagename: '',
        packagedescription: '',
        packageprice: 0,
        packageimg: ""
    })

    // Initialise states for errors
    const [errors, setErrors] = useState({
        packagename: '',
        packagedescription: '',
        packageprice: '',
        packageimg: ''
    });

    const toaster = new MessageToaster();

    useEffect(() => {
        handleUpdate()
        // To get details by id
        if (props.edit) {
            getPackageDetails()
        }
    }, [])

    // Update the data
    const handleUpdate = async () => {
        await updateData({ heading: props.edit ? "Edit Package" : "Marketing Package-Add New Package", url: `/superadmin/${pageURLs.marketing_package}`, showBackButton: true, showMainMenu: true, activeLink: "manage-hardware" });
    };

    // Navigate accordung to url
    const itemClick = async (url: string) => {
        navigate(url);
    }


    // To get package details in edit page
    const getPackageDetails = async () => {
        try {

            const { data } = await axios.get(PackageApi.GET_PACKAGE_DETAILS + params.id);
            if (data.error === 0) {
                const packageDetail = data.data

                setFormData({
                    packagename: packageDetail.packageName,
                    packagedescription: packageDetail.packageDesc,
                    packageprice: packageDetail.packagePrice,
                    packageimg: packageDetail.packageImage
                })

            }
            else {
                // show error message
                toaster.toastError(data.message);
            }
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    // Triggers on submit
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setLoading(true)
        try {
            // Validate form data
            await addPackageSchema.validate(formData, { abortEarly: false });

            //Clear errors
            setErrors({
                packagename: '',
                packagedescription: '',
                packageprice: '',
                packageimg: ''
            });

            try {
                setLoading(true)

                // In case of edit
                if (props.edit) {
                    const updatedformData = {

                        packageId: params.id ? parseInt(params.id) : 0,
                        packageName: formData.packagename,
                        packagePrice: Number(formData.packageprice),
                        packageImage: formData.packageimg,
                        packageDesc: formData.packagedescription

                    }
                    const { data } = await axios.post(PackageApi.MODIFY_PACKAGE, updatedformData);
                    if (data?.error === 0) {
                        navigate(`/superadmin/${pageURLs.marketing_package}`)

                    }
                    else {
                        // show error message
                        toaster.toastError(data?.message);
                    }
                } else {

                    const newformData = {
                        packageName: formData.packagename,
                        packagePrice: Number(formData.packageprice),
                        packageImage: formData.packageimg,
                        packageDesc: formData.packagedescription
                    }
                    const { data } = await axios.post(PackageApi.CREATE_PACKAGE, newformData);
                    if (data?.error === 0) {
                        navigate(`/superadmin/${pageURLs.marketing_package}`)

                    }
                    else {
                        // show error message
                        toaster.toastError(data?.message);
                    }
                }

            } catch (error: any) {
                throw new Error(error?.response?.data?.message || error?.message);
            }
            finally {
                setLoading(false); // Hide loader
            }

        } catch (error) {
            const newErrors: any = {};

            // If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            }

        } finally {
            setLoading(false); // Hide loader
        }

    }

    // Triggers whenever input is entered
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = event.target

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }
        if (name == 'packageprice') {
            setFormData({
                ...formData,
                [name]: parseFloat(value),
            });
        }
        else {
            setFormData({
                ...formData,
                [name]: value,
            });
        }

    }

    //Upload Image
    const uploadImage = async (e: any) => {
        setErrors((prev) => ({
            ...prev,
            packageimg: ''
        }));
        if (e.target.files && e.target.files[0]) {
            const img = e.target.files[0];
            const isValid = validateImageTypes(img);
            if (isValid) {
                const folderName = "file/"
                const randomNumber = Math.floor(Math.random() * 1000000);
                const extension = img.name.substring(img.name.lastIndexOf("."));
                const newImageName = randomNumber + extension;
                setLoading(true)

                const request_params = {
                    key: folderName + newImageName
                }

                const response = await axios.post(CommonApi.PRESIGNED_URL, request_params);
                if (response?.data?.error === 0) {
                    const uploadURL = response.data.data
                    if (uploadURL) {
                        try {
                            // PUT request: upload file to S3
                            const result = await fetch(uploadURL, {
                                method: "PUT",
                                body: img,
                            });
                            if (result.status === 200) {
                                setLoading(false);
                                setFormData({
                                    ...formData,
                                    ['packageimg']: folderName + newImageName,

                                });


                            } else {
                                setLoading(false)
                            }
                        } catch (e) {
                            setLoading(false)
                            console.log(e)
                        }
                    } else {
                        setLoading(false)
                    }
                } else {
                    setLoading(false)
                    setErrors((prev) => ({
                        ...prev,
                        packageimg: response?.data?.message
                    }));
                }

            } else {
                setLoading(false)

                setErrors((prev) => ({
                    ...prev,
                    packageimg: 'Upload image files with .png or .jpeg extensions only.'
                }));
            }
        }
    }

    const removeFile = async () => {
        setFormData({
            ...formData,
            ['packageimg']: ''
        })
    }

    return (

        <div className='hardware-listing-container addnew-item-wrap'>
            <Container>
                <div className="nav-btn-title-sec d-flex justify-content-between align-items-center">
                    <Breadcrumb>
                        <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.manage_hardware}`) }}>Hardware Listing</Breadcrumb.Item>
                        <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.manage_hardware}`) }}> Manage Hardware</Breadcrumb.Item>
                        <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.marketing_package}`) }}> Marketing Package</Breadcrumb.Item>
                        <Breadcrumb.Item active>{props.edit ? "Edit Package" : "Add New Package"}</Breadcrumb.Item>
                    </Breadcrumb>
                </div>
                <h6 className='f-bold title'>{props.edit ? "Edit Marketing Package" : "Add Marketing Package"}</h6>
                <form onSubmit={(e) => handleSubmit(e)} >

                    <div className="hardware-list-wrap">
                        <div className="form-inner mx-auto">
                            <Row>
                                <Col md={6}>
                                    <CWInput
                                        id="packagename"
                                        name="packagename"
                                        type="text"
                                        label="Package Name"
                                        value={formData.packagename}
                                        isMandatory
                                        maxLength={PACKAGE_NAME_LENGTH}
                                        errorMessage={errors.packagename}
                                        onChange={handleInputChange}

                                    />
                                </Col>
                                <Col md={6}>
                                    <CWInputGroup
                                        id="packageprice"
                                        name="packageprice"
                                        type="number"
                                        label="Package Price"
                                        labelPrefix="$"
                                        value={formData.packageprice}
                                        maxLength={PACKAGE_PRICE_LENGTH}
                                        errorMessage={errors.packageprice}
                                        onChange={handleInputChange}
                                        isMandatory
                                    /></Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <CWTextarea
                                        id="packagedescription"
                                        name="packagedescription"
                                        type="text"
                                        label="Package Description"
                                        value={formData.packagedescription}
                                        errorMessage={errors.packagedescription}
                                        maxLength={PACKAGE_DESCRIPTION_LENGTH}
                                        onChange={handleInputChange}
                                        isMandatory
                                    />
                                </Col>
                                <Col md={6}>
                                    <div className="file-uploader-wrap">
                                        <div className="file-upload d-flex align-items-center justify-content-center">
                                            <div className="upload-info text-center">
                                                {formData.packageimg && <div className="preview-logo">
                                                    <div className="common-cover d-flex align-items-center justify-content-center">
                                                        <Button variant="light" className="light-btn close" onClick={removeFile}><CloseIcon /></Button>
                                                        <img src={`${process.env.REACT_APP_S3_URL_PUBLIC_PATH || ""}${formData.packageimg}`} alt="Car Wash App" width={"139px"} height={"139px"} />
                                                    </div>
                                                </div>}
                                                {!formData.packageimg && <> <img src={Fileimgicon} alt="" />
                                                    <p className='f-semi'>Upload Image</p></>}
                                            </div>
                                            <Form.Control type="file" multiple className='file-input' onChange={uploadImage} />

                                        </div>
                                        <span className="upload-note">Note: Accepted image formats are .jpeg and .png</span>
                                    </div>
                                    {errors.packageimg && (
                                        <span className="error-msg">{errors.packageimg}</span>
                                    )}
                                </Col>
                            </Row>
                        </div>
                    </div>
                    <div className="btn-sec d-flex justify-content-end">
                        <Button title="Cancel" variant="primary" className="secondary-btn" onClick={() => navigate(`/superadmin/${pageURLs.marketing_package}`)}>Cancel</Button>
                        <Button title="Save" variant="primary" className="primary-btn" type="submit" >Save</Button>
                    </div>
                </form>

            </Container >
            {loading && <SmallLoader />}

        </div >
    )
}

export default AddNewPackage;
